import { useCallback, useEffect, useState } from 'react';
import { useT } from '../state/i18n.js';

// 공유 링크 → QR. 완전 클라이언트(백엔드·인프라 0). 오프라인 배포(인쇄물·부스·명함) 표면을
// 추적링크에 연결한다. QR 이 인코딩하는 URL 에 ?src=qr 를 병합해 두면, 뷰어 트래커가 매 이벤트에
// pageUrl(location.href) 을 실어 보내므로 스캔 유입이 그대로 분석에 남는다(서버 변경 불필요).

/** shareUrl 에 src=qr 을 병합(기존 쿼리 보존). 실패 시 원본 반환. */
export function withQrSource(shareUrl: string): string {
  try {
    const url = new URL(shareUrl);
    url.searchParams.set('src', 'qr');
    return url.toString();
  } catch {
    return shareUrl;
  }
}

/** 파일명에 못 쓰는 문자를 정리(다운로드용). */
function safeFileName(alias: string): string {
  const base = (alias || 'link').trim().replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '');
  return base || 'link';
}

export function QrButton({ url, alias }: { url: string; alias: string }): JSX.Element {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [svg, setSvg] = useState<string | null>(null);
  const [png, setPng] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const target = withQrSource(url);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setError(null);
    setSvg(null);
    setPng(null);
    // qrcode 는 QR 을 실제로 열 때만 동적 로드 — 초기 번들에서 ~40KB 빠짐(대다수는 안 누른다).
    // 인쇄 대비 여백(margin) 여유, 오류정정 M(중간) — 로고 없이 스캔 신뢰성/밀도 균형.
    void import('qrcode')
      .then(({ default: QRCode }) =>
        Promise.all([
          QRCode.toString(target, { type: 'svg', margin: 2, errorCorrectionLevel: 'M', width: 320 }),
          QRCode.toDataURL(target, { margin: 2, errorCorrectionLevel: 'M', width: 1024 }),
        ]),
      )
      .then(([svgStr, pngUrl]) => {
        if (!alive) return;
        setSvg(svgStr);
        setPng(pngUrl);
      })
      .catch(() => {
        if (alive) setError(t({ ko: 'QR 생성에 실패했습니다.', en: 'Could not generate QR code.' }));
      });
    return () => {
      alive = false;
    };
  }, [open, target, t]);

  // Esc 로 닫기.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const download = useCallback(
    (href: string, ext: string) => {
      const a = document.createElement('a');
      a.href = href;
      a.download = `qr-${safeFileName(alias)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [alias],
  );

  const downloadSvg = useCallback(() => {
    if (!svg) return;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const href = URL.createObjectURL(blob);
    download(href, 'svg');
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  }, [svg, download]);

  return (
    <>
      <button
        type="button"
        className="link qr-btn"
        onClick={() => setOpen(true)}
        aria-label={t({ ko: 'QR 코드 만들기', en: 'Create QR code' })}
        data-testid="qr-open"
      >
        {t({ ko: 'QR', en: 'QR' })}
      </button>

      {open ? (
        <div
          className="qr-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t({ ko: 'QR 코드', en: 'QR code' })}
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="qr-modal">
            <header className="qr-modal-head">
              <h3>{t({ ko: 'QR 코드', en: 'QR code' })}</h3>
              <button
                type="button"
                className="qr-close"
                aria-label={t({ ko: '닫기', en: 'Close' })}
                onClick={() => setOpen(false)}
              >
                ✕
              </button>
            </header>

            {error ? (
              <p className="status err">{error}</p>
            ) : svg ? (
              <div
                className="qr-canvas"
                data-testid="qr-canvas"
                // 자체 생성한 SVG 문자열(사용자 입력 아님) — 안전.
                dangerouslySetInnerHTML={{ __html: svg }}
              />
            ) : (
              <p className="muted">{t({ ko: '생성 중…', en: 'Generating…' })}</p>
            )}

            <p className="qr-target muted small" data-testid="qr-target">
              {target}
            </p>
            <p className="muted small">
              {t({
                ko: '스캔 유입은 분석에서 "QR"(src=qr) 소스로 남습니다. 인쇄물·부스·명함에 쓰세요.',
                en: 'Scans are tracked as the "QR" source (src=qr) in analytics. Use it on print, booths, or cards.',
              })}
            </p>

            <div className="qr-actions">
              <button
                type="button"
                className="cta-secondary"
                disabled={!png}
                onClick={() => png && download(png, 'png')}
                data-testid="qr-download-png"
              >
                {t({ ko: 'PNG 저장', en: 'Download PNG' })}
              </button>
              <button
                type="button"
                className="cta-secondary"
                disabled={!svg}
                onClick={downloadSvg}
                data-testid="qr-download-svg"
              >
                {t({ ko: 'SVG 저장 (인쇄용)', en: 'Download SVG (print)' })}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
